import { createMemo, createSignal } from "solid-js";

import {
  CheckUserAccExists,
  GetPhoneVerifyCode,
  NewForgetCodeSubmit,
  VerifyCodeNewPassword,
  useVerifyCodeEmail,
} from "@/api/clientApi/login.ts";
import { message } from "@/components/Base/Message/Message";
import { DialogType } from "@/components/Dialogs/types";
import { useDialog } from "@/components/Dialogs/useDialog";
import { LocalStorageKey, PHONE_CODE } from "@/constants/constants.ts";
import { CodeTypeEnum } from "@/enum/loginRegister";
import { useRouter } from "@/hooks/useRouter";
import { getLangKey, t } from "@/i18n";
import { rsaEncrypt } from "@/lib/aesUtils.ts";
import { useTimestampInSeconds } from "@/lib/hooks/useTimestamp";
import { removeLocalStorageItem } from "@/lib/localStorage.ts";
import { sdkManager } from "@/lib/sdk/sdkManager";
import { addZero, getAppType, getOS } from "@/lib/utils";
import {
  // isValidCPF,
  isValidEmail,
  isValidPassword,
  isValidPhone,
} from "@/lib/validateUtils";
import { sysConfigStore } from "@/stores/sysConfigStore";
import { CheckUserAccExistsReq } from "@/types/swagger/loginV2";

export type ResetMode =
  | "find"
  | "email"
  | "phone"
  // | "cpf"
  // | "cpfReset"
  | "emailReset"
  // | "cpfReset"
  | "phoneReset";

export interface ResetState {
  mode: ResetMode;
  email: string;
  phone: string;
  acode: string;
  // cpf: string;
  code: string;
  password: string;
  confirmPassword: string;
  phoneRetry: number;
  inputError: Record<string, string>;
  endTime: number;
  isExpired: boolean;
}
export const [mode, setMode] = createSignal<ResetMode>("email");
export const [code, setCode] = createSignal<string>("");
export const [email, setEmail] = createSignal<string>("");
export const [phone, setPhone] = createSignal<string>("");
export const [acode, setAcode] = createSignal<string>(PHONE_CODE);
// export const [cpf, setCpf] = createSignal<string>("");
export const [password, setPassword] = createSignal<string>("");
export const [confirmPassword, setConfirmPassword] = createSignal<string>("");
export const [phoneRetry, setPhoneRetry] = createSignal<number>(0);
const [inputError, setInputError] = createSignal<Record<string, string>>({});
export const [endTime, setEndTime] = createSignal<number>(0);
export const [isExpired, setIsExpired] = createSignal<boolean>(false);
export const [loading, setLoading] = createSignal<boolean>(false);
export const [submitLoading, setSubmitLoading] = createSignal<boolean>(false);
export const useReset = () => {
  const { push } = useRouter();
  const { showDialog } = useDialog();

  // 输入验证
  const validInput = (key: keyof ResetState): string => {
    // 清除特定字段的错误
    setInputError((prev) => ({ ...prev, [key]: "" }));

    switch (key) {
      case "email":
        if (!email()) {
          setInputError((prev) => ({
            ...prev,
            email: t("auth.emailrequired"),
          }));
          return t("auth.emailrequired");
        } else if (!isValidEmail(email())) {
          setInputError((prev) => ({
            ...prev,
            email: t("auth.emailinvalid"),
          }));
          return t("auth.emailinvalid");
        } else if (isValidEmail(email())) {
          sdkManager.tdReportEvent("recover_password_page", {
            email_form_field_validate: `${getAppType()}_${getOS()}`,
          });
        }
        return "";
      case "phone":
        if (!phone()) {
          setInputError((prev) => ({
            ...prev,
            phone: t("auth.phonerequired"),
          }));
          return t("auth.phonerequired");
        } else if (!isValidPhone(acode(), phone())) {
          setInputError((prev) => ({
            ...prev,
            phone: t("auth.phoneinvalid"),
          }));
          return t("auth.phoneinvalid");
        } else if (isValidPhone(acode(), phone())) {
          sdkManager.tdReportEvent("recover_password_page", {
            phone_form_field_validate: `${getAppType()}_${getOS()}`,
          });
        }
        return "";
      // case "cpf":
      //   if (!cpf()) {
      //     setInputError((prev) => ({
      //       ...prev,
      //       cpf: t("auth.cpfrequired"),
      //     }));
      //     return t("auth.cpfrequired");
      //   } else if (!isValidCPF(cpf())) {
      //     setInputError((prev) => ({
      //       ...prev,
      //       cpf: t("auth.cpfinvalid"),
      //     }));
      //     return t("auth.cpfinvalid");
      //   }
      //   return "";
      case "code":
        if (!code()) {
          setInputError((prev) => ({
            ...prev,
            code: t("auth.coderequired"),
          }));
          return t("auth.coderequired");
        }
        return "";
      case "password":
        if (!password()) {
          setInputError((prev) => ({
            ...prev,
            password: t("auth.passwordrequired"),
          }));
          return t("auth.passwordrequired");
        } else if (!isValidPassword(password())) {
          setInputError((prev) => ({
            ...prev,
            password: t("auth.passwordinvalid"),
          }));
          return t("auth.passwordinvalid");
        }
        return "";
      case "confirmPassword":
        if (!confirmPassword()) {
          setInputError((prev) => ({
            ...prev,
            confirmPassword: t("auth.confirm_password_required"),
          }));
          return t("auth.confirm_password_required");
        } else if (confirmPassword() !== password()) {
          setInputError((prev) => ({
            ...prev,
            confirmPassword: t("auth.confirm_password_invalid"),
          }));
          return t("auth.confirm_password_invalid");
        }
        return "";
      default:
        return "";
    }
  };

  //检查账号是否已经存在
  const checkAccountExists = async (type: "email" | "phone") => {
    const req: CheckUserAccExistsReq = {
      // account: "",
      email: "",
      phone: "",
      a_code: "",
    };

    if (type === "email") {
      req.email = await rsaEncrypt(email());
    } else {
      req.phone = await rsaEncrypt(phone());
      req.a_code = acode();
    }

    const res = await CheckUserAccExists(req);
    if (res?.exists) {
      if (resetStore().mode === "find") {
        setInputError((prev) => ({
          ...prev,
          [type]: t("tops.error_1314"),
        }));
      }
      return true;
    }
    return false;
  };

  // 提交处理
  const submit = async (key: string, replaceFn?: (path: string) => void) => {
    setLoading(true);
    switch (key) {
      case "email":
        if (!validInput("email")) {
          const res = await useVerifyCodeEmail({
            email: await rsaEncrypt(email()),
            url: window.location.origin,
            device: {
              UA: navigator.userAgent,
              sign: "web",
              client_type: 0,
              app_type: 0,
              m_app: 0,
            },
            lang: getLangKey(),
            host: window.location.host,
            logo: `/${__APP_NAME__}/images/logo/favicon.png`,
          }).finally(() => {
            setLoading(false);
          });
          if (res && replaceFn) {
            replaceFn("login");
            showDialog({
              type: DialogType.EmailSucceed,
              data: { email: email() },
            });
          }
        }
        setLoading(false);
        return;
      case "phone":
        if (!validInput("phone")) {
          setPhoneRetry(0);
          getVerifyCode();
        }
        setLoading(false);
        return;
      // case "cpf":
      //   if (!validInput("cpf")) {
      //     console.log("submit");
      //   }
      //   setLoading(false);
      //   return;
      default: {
        setSubmitLoading(true);
        if (key === "phoneReset" && validInput("code")) {
          setSubmitLoading(false);
          setLoading(false);
          return;
        }
        if (validInput("password") || validInput("confirmPassword")) {
          setSubmitLoading(false);
          return;
        }

        const params: {
          code: string;
          phone?: string;
          acode?: string;
          email?: string;
        } = {
          code: code(),
        };

        if (key === "phoneReset") {
          params.phone = await rsaEncrypt(phone());
          params.acode = acode();
        } else {
          params.email = await rsaEncrypt(email());
        }

        const res = await VerifyCodeNewPassword(params).finally(() => {
          setSubmitLoading(false);
        });
        if (res && replaceFn) {
          const { default: SparkMD5 } = await import("@repo/sparkmd5");
          const md5Pwd = SparkMD5.hash(confirmPassword());
          const res2 = await NewForgetCodeSubmit({
            key: res.data,
            new_pwd: md5Pwd,
          });
          if (res2) {
            removeLocalStorageItem(LocalStorageKey.REMEMBER_ME);
            replaceFn("login");
            message.show(t("auth.reset_success"));
          }
        }
        return;
      }
    }
  };

  // 获取验证码
  const getVerifyCode = async () => {
    const isExists = await checkAccountExists("phone");
    if (!isExists) {
      message.show(t("tops.error_1319"));
      return;
    }

    const res = await GetPhoneVerifyCode({
      phone: await rsaEncrypt(phone()),
      acode: acode(),
      verify_type: 0,
      code_type: CodeTypeEnum.CodeTypeUpdatePwd,
      retry: phoneRetry(),
      device: {
        UA: navigator.userAgent,
        sign: "web",
        client_type: 0,
        app_type: 0,
        m_app: 0,
      },
      lang: getLangKey(),
      email: email(),
    });
    if (res) {
      setMode("phoneReset");
      setEndTime(
        Date.now() / 1000 +
          sysConfigStore.sysConfigData.sending_interval.phone_interval,
      );
      setPhoneRetry(1);
      message.show(t("tips.code_sent"));
    }
  };

  // 处理邮件链接
  const handleEmailLink = () => {
    const urlParams: { [key: string]: string } = {};
    const urlObj = new URL(window.location.href);
    urlObj.searchParams.forEach((value, key) => {
      urlParams[key] = value;
    });
    if (urlParams.email && urlParams.code) {
      setEmail(urlParams.email);
      const now = Math.ceil(Date.now() / 1000);
      if (Number(urlParams.expired) > now) {
        setCode(urlParams.code);
        setMode("emailReset");
      } else {
        setMode("email");
        setIsExpired(true);
      }
      push("reset");
    }
  };

  // 倒计时
  const countDown = createMemo(() => {
    const timeStamp = useTimestampInSeconds();
    const currentEndTime = endTime();
    if (!currentEndTime) return null;

    const total = Math.max(Math.floor(currentEndTime - timeStamp()), 0);
    if (!total) {
      setEndTime(0);
      return null;
    }
    return addZero(total);
  });

  // 重置状态
  const resetState = () => {
    setLoading(false);
    setSubmitLoading(false);
    setMode("find");
    setEmail("");
    setPhone("");
    setAcode(PHONE_CODE);
    // setCpf("");
    setCode("");
    setPassword("");
    setConfirmPassword("");
    setPhoneRetry(0);
    setInputError({});
    setEndTime(0);
    setIsExpired(false);
  };

  // 设置状态的便捷方法
  const setResetStore = (
    key: keyof ResetState,
    value: ResetMode | string | number | boolean,
  ) => {
    switch (key) {
      case "mode":
        setMode(value as ResetMode);
        break;
      case "email":
        setEmail(value as string);
        break;
      case "phone":
        setPhone(value as string);
        break;
      case "acode":
        setAcode(value as string);
        break;
      // case "cpf":
      //   setCpf(value as string);
      //   break;
      case "code":
        setCode(value as string);
        break;
      case "password":
        setPassword(value as string);
        break;
      case "confirmPassword":
        setConfirmPassword(value as string);
        break;
      case "phoneRetry":
        setPhoneRetry(value as number);
        break;
      case "endTime":
        setEndTime(value as number);
        break;
      case "isExpired":
        setIsExpired(value as boolean);
        break;
    }
  };

  const resetStore = () => ({
    mode: mode(),
    email: email(),
    phone: phone(),
    acode: acode(),
    // cpf: cpf(),
    code: code(),
    password: password(),
    confirmPassword: confirmPassword(),
    phoneRetry: phoneRetry(),
    inputError: inputError(),
    endTime: endTime(),
    isExpired: isExpired(),
  });

  return {
    // 状态
    // loading,
    // submitLoading,
    // setLoading,
    // setSubmitLoading,
    // email,
    // phone,
    // acode,
    // cpf,
    // code,
    // password,
    // confirmPassword,
    // phoneRetry,
    // inputError,
    // endTime,
    // isExpired,

    // 兼容性对象
    resetStore,
    setResetStore,

    // 方法
    validInput,
    submit,
    getVerifyCode,
    handleEmailLink,
    countDown,
    resetState,

    // 直接设置方法
    // setEmail,
    // setPhone,
    // setAcode,
    // setCpf,
    // setCode,
    // setPassword,
    // setConfirmPassword,
  };
};
